/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useEffect } from "react";

import {
  getLoggedinUser,
  WEBSITE_AUTH_STORAGE_KEY,
} from "../helpers/api_helper";
import { helperFunction } from "../helpers/api_call_helper";
import {
  getUserById,
  unsubscribeSubscription as unsubscribeSubscriptionApi,
  updateProfile as updateProfileApi,
} from "../helpers/backend_helper";
const AuthContext = createContext(null);

const getAuthPayload = (response) => response?.data?.data || response?.data || response;

const buildSession = (payload) => ({
  user: payload?.user || null,
  accessToken: payload?.accessToken || "",
});

const persistSession = (session) => {
  localStorage.setItem(WEBSITE_AUTH_STORAGE_KEY, JSON.stringify(session));
};

const getUserId = (value) => value?.id || value?._id || "";

const subscribeWebsiteAnnouncementTopic = async () => {
  const notifications = await import("../services/notificationService");
  return notifications.subscribeWebsiteAnnouncementTopic();
};

const unsubscribeWebsiteAnnouncementTopic = async () => {
  const notifications = await import("../services/notificationService");
  return notifications.unsubscribeWebsiteAnnouncementTopic();
};

const hasActiveSubscription = (user) => {
  if (!user || user.subscriptionType === "free") return false;
  const expiry = user.subscriptionExpiryDate ? new Date(user.subscriptionExpiryDate) : null;
  return Boolean(user.isSubscribed && expiry && expiry.getTime() > Date.now());
};

export function AuthProvider({ children }) {
  const savedSession = getLoggedinUser();
  const isSuspended = savedSession?.user?.isActive === false;

  if (isSuspended) {
    localStorage.removeItem(WEBSITE_AUTH_STORAGE_KEY);
  }

  const [user, setUser] = useState(isSuspended ? null : (savedSession?.user || null));
  const [isLoggedIn, setIsLoggedIn] = useState(isSuspended ? false : Boolean(savedSession?.accessToken));
  const [isSubscribed, setIsSubscribed] = useState(hasActiveSubscription(savedSession?.user));
  const [authError, setAuthError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSubGate, setShowSubGate] = useState(false);
  const [subGateContext, setSubGateContext] = useState(null);

  const applySessionUser = useCallback((nextUser) => {
    const currentSession = getLoggedinUser();
    if (!currentSession?.accessToken || !nextUser) return null;

    const nextSession = {
      ...currentSession,
      user: {
        ...(currentSession.user || {}),
        ...nextUser,
      },
    };

    persistSession(nextSession);
    setUser(nextSession.user);
    setIsSubscribed(hasActiveSubscription(nextSession.user));
    return nextSession.user;
  }, []);

  const saveAuth = useCallback((payload) => {
    const session = buildSession(payload);

    persistSession(session);
    setUser(session.user);
    setIsLoggedIn(Boolean(session.accessToken));
    setIsSubscribed(hasActiveSubscription(session.user));
    if (session.accessToken) {
      subscribeWebsiteAnnouncementTopic();
    }
    return session;
  }, []);

  const refreshUser = useCallback(async () => {
    const currentSession = getLoggedinUser();
    const userId = getUserId(currentSession?.user);

    if (!currentSession?.accessToken || !userId) return null;

    const response = await getUserById(userId);
    const nextUser = getAuthPayload(response);
    return applySessionUser(nextUser);
  }, [applySessionUser]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    refreshUser().catch(() => {});

    const intervalId = window.setInterval(() => {
      refreshUser().catch(() => {});
    }, 60000);

    const handleFocus = () => {
      refreshUser().catch(() => {});
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
    };
  }, [isLoggedIn, refreshUser]);

  const openAuthModal = useCallback(() => setShowAuthModal(true), []);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
    setAuthError(null);
  }, []);

  const openSubGate = useCallback((context = null) => {
    setSubGateContext(context);
    setShowSubGate(true);
  }, []);

  const closeSubGate = useCallback(() => {
    setShowSubGate(false);
    setSubGateContext(null);
  }, []);

  const subscribe = useCallback(async (planId = "premium", subscriptionUser = null) => {
    const currentSession = getLoggedinUser();
    const nextUser = subscriptionUser
      ? { ...(currentSession?.user || {}), ...subscriptionUser }
      : {
          ...(currentSession?.user || {}),
          isSubscribed: true,
          subscriptionType: planId,
        };
    const nextSession = {
      ...currentSession,
      user: nextUser,
    };

    persistSession(nextSession);
    setUser(nextUser);
    setIsSubscribed(hasActiveSubscription(nextUser));
    setShowSubGate(false);
    return nextSession;
  }, []);

  const logout = useCallback(async () => {
    await unsubscribeWebsiteAnnouncementTopic();
    localStorage.removeItem(WEBSITE_AUTH_STORAGE_KEY);
    setUser(null);
    setIsLoggedIn(false);
    setIsSubscribed(false);
    setAuthError(null);
    setShowSubGate(false);
    window.location.href = '/';
  }, []);

  const updateProfile = useCallback(async (updates) => {
    setAuthError(null);
    const updatedUser = await helperFunction(
      async () => {
        const response = await updateProfileApi(updates);
        const nextUser = getAuthPayload(response);
        const currentSession = getLoggedinUser();
        const nextSession = {
          ...currentSession,
          user: nextUser,
        };

        persistSession(nextSession);
        setUser(nextUser);
        return nextUser;
      },
      () => {},
      "",
      setAuthError,
    );

    return updatedUser;
  }, []);

  const unsubscribeSubscription = useCallback(async () => {
    setAuthError(null);
    const updatedUser = await helperFunction(
      async () => {
        const response = await unsubscribeSubscriptionApi();
        const nextUser = getAuthPayload(response);
        return applySessionUser(nextUser);
      },
      () => {},
      "",
      setAuthError,
    );

    return updatedUser;
  }, [applySessionUser]);

  const value = {
    user,
    isLoggedIn,
    isSubscribed,
    authError,
    showAuthModal,
    showSubGate,
    subGateContext,
    saveAuth,
    subscribe,
    logout,
    setAuthError,
    openAuthModal,
    closeAuthModal,
    openSubGate,
    closeSubGate,
    updateProfile,
    unsubscribeSubscription,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
